import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import { useMemo } from 'react'

import { apiFetch } from '@/lib/api'
import {
  lerTrechos,
  type ModoDoTooltip,
  type PosicaoDoTooltip,
  type TamanhoDoTooltip,
  type Trecho,
} from '@/lib/tooltips'

/*
 * O tooltip no observatório de verdade, aberto pela Área do Gestor antes de
 * ir para o público:
 *
 * - `?posicionarTooltip=true`: o editor arrasta até o lugar o tooltip que está
 *   escrevendo. O texto e a aparência vêm do rascunho que a gestão guardou na
 *   aba (`sessionStorage`); nada vai ao servidor. "Concluir" grava a posição
 *   nesse rascunho e volta ao quadro; "Voltar" volta sem mudá-la.
 * - `?tooltipEmTeste={id}`: o administrador vê, no lugar, o tooltip que
 *   aguarda teste, lido da API com a sessão, e conclui o teste, o passo que
 *   libera a publicação.
 *
 * A posição é uma porcentagem da área do observatório (a janela abaixo do
 * cabeçalho), com uma casa decimal: o mesmo número vale em qualquer largura.
 * É o centro do ícone *i* ou do ponto do "passar".
 */

/** O que o observatório está fazendo com um tooltip que o público ainda não vê. */
export type ModoNoObservatorio = { tipo: 'posicionar' } | { tipo: 'teste'; tooltip: number }

/** Os parâmetros do tooltip como ficam na URL do mapa. */
export interface ModoNoObservatorioNaUrl {
  posicionarTooltip?: true
  tooltipEmTeste?: number
}

const ID = /^[1-9]\d{0,17}$/

function lerId(bruto: unknown): number | undefined {
  if (typeof bruto !== 'number' && typeof bruto !== 'string') return undefined
  const texto = String(bruto)
  if (!ID.test(texto)) return undefined
  const id = Number(texto)
  return Number.isSafeInteger(id) ? id : undefined
}

/**
 * Lê o modo do tooltip de uma URL e descarta o que não faz sentido. O teste
 * vence o posicionamento quando os dois vêm juntos: é o que pede dado à API,
 * e o posicionamento sem a gestão por trás não tem para onde voltar.
 */
export function lerModoDoTooltip(bruto: Record<string, unknown>): ModoNoObservatorio | undefined {
  const tooltip = lerId(bruto.tooltipEmTeste)
  if (tooltip !== undefined) return { tipo: 'teste', tooltip }
  const bandeira = bruto.posicionarTooltip
  if (bandeira === true || bandeira === 'true') return { tipo: 'posicionar' }
  return undefined
}

export function escreverModoDoTooltip(
  modo: ModoNoObservatorio | undefined
): ModoNoObservatorioNaUrl {
  if (modo === undefined) return {}
  return modo.tipo === 'teste' ? { tooltipEmTeste: modo.tooltip } : { posicionarTooltip: true }
}

/** O modo do tooltip da URL do mapa, ou `undefined` no observatório comum. */
export function useModoNoObservatorio(): ModoNoObservatorio | undefined {
  const busca: Record<string, unknown> = useSearch({ strict: false })
  const modo = lerModoDoTooltip(busca)
  const tipo = modo?.tipo
  const id = modo?.tipo === 'teste' ? modo.tooltip : undefined
  // Mesmo modo, mesmo objeto: quem depende dele num efeito não roda à toa.
  return useMemo((): ModoNoObservatorio | undefined => {
    if (tipo === 'posicionar') return { tipo }
    return id === undefined ? undefined : { tipo: 'teste', tooltip: id }
  }, [tipo, id])
}

// ── A posição ────────────────────────────────────────────────────────────────

/*
 * Os limites deixam o tooltip inteiro à vista e longe das bordas, onde ficam
 * os controles do mapa e as abas dos painéis: 4 a 96 na horizontal, 8 a 92 na
 * vertical.
 */
const LIMITE_X = [4, 96] as const
const LIMITE_Y = [8, 92] as const

/** Onde o tooltip começa quando o rascunho ainda não tem posição. */
export const POSICAO_PADRAO: PosicaoDoTooltip = { x: 40, y: 46 }

function umaCasa(valor: number): number {
  return Math.round(valor * 10) / 10
}

function entre(valor: number, [minimo, maximo]: readonly [number, number]): number {
  return Math.min(maximo, Math.max(minimo, valor))
}

/** A posição dentro dos limites, com uma casa decimal. */
export function prenderPosicao({ x, y }: PosicaoDoTooltip): PosicaoDoTooltip {
  return { x: umaCasa(entre(x, LIMITE_X)), y: umaCasa(entre(y, LIMITE_Y)) }
}

/** A posição movida pelas setas, em pontos percentuais. */
export function moverPosicao(posicao: PosicaoDoTooltip, dx: number, dy: number): PosicaoDoTooltip {
  return prenderPosicao({ x: posicao.x + dx, y: posicao.y + dy })
}

interface Retangulo {
  left: number
  top: number
  width: number
  height: number
}

/** O ponto da tela (em px) como posição na área; área sem tamanho não tem posição. */
export function posicaoNaArea(
  ponto: { x: number; y: number },
  area: Retangulo
): PosicaoDoTooltip | undefined {
  if (area.width <= 0 || area.height <= 0) return undefined
  return prenderPosicao({
    x: ((ponto.x - area.left) / area.width) * 100,
    y: ((ponto.y - area.top) / area.height) * 100,
  })
}

const UMA_CASA = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

/** O que o leitor de tela ouve a cada movimento. */
export function anunciarPosicao({ x, y }: PosicaoDoTooltip): string {
  return `Posição: ${UMA_CASA.format(x)}% da largura, ${UMA_CASA.format(y)}% da altura.`
}

// ── O rascunho da aba ────────────────────────────────────────────────────────

/** Onde a Área do Gestor guarda o tooltip que está sendo escrito, na aba. */
export const CHAVE_DO_RASCUNHO = 'gef:rascunho-tooltip'

const MODOS: readonly ModoDoTooltip[] = ['icone', 'passar']
const TAMANHOS: readonly TamanhoDoTooltip[] = ['menor', 'padrao', 'maior']

/** O que o posicionamento lê do rascunho: o texto, a aparência e a posição. */
export interface RascunhoParaPosicionar {
  trechos: Trecho[]
  modo: ModoDoTooltip
  tamanho: TamanhoDoTooltip
  posicao: PosicaoDoTooltip | null
}

function lerPosicao(valor: unknown): PosicaoDoTooltip | null | undefined {
  if (valor === undefined || valor === null) return null
  if (typeof valor !== 'object') return undefined
  const { x, y } = valor as Record<string, unknown>
  const dentro = (n: unknown): n is number =>
    typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 100
  return dentro(x) && dentro(y) ? { x, y } : undefined
}

/**
 * O rascunho bruto, como objeto. O armazenamento da aba pode não existir ou
 * lançar erro (janela privada, dado do site bloqueado): aí não há rascunho.
 */
function lerObjetoDoRascunho(): Record<string, unknown> | undefined {
  try {
    const bruto = window.sessionStorage.getItem(CHAVE_DO_RASCUNHO)
    if (bruto === null) return undefined
    const valor: unknown = JSON.parse(bruto)
    return typeof valor === 'object' && valor !== null && !Array.isArray(valor)
      ? (valor as Record<string, unknown>)
      : undefined
  } catch {
    return undefined
  }
}

/** O rascunho pronto para posicionar, ou `undefined` se não há o que posicionar. */
export function lerRascunhoParaPosicionar(): RascunhoParaPosicionar | undefined {
  const objeto = lerObjetoDoRascunho()
  if (objeto === undefined) return undefined
  const trechos = lerTrechos(objeto.trechos)
  if (trechos === undefined || trechos.every((t) => t.texto.trim() === '')) return undefined
  const modo = MODOS.find((m) => m === objeto.modo)
  const tamanho = TAMANHOS.find((t) => t === objeto.tamanho)
  if (modo === undefined || tamanho === undefined) return undefined
  // Posição estranha no rascunho não impede posicionar: começa sem ela.
  const posicao = lerPosicao(objeto.posicao) ?? null
  return { trechos, modo, tamanho, posicao }
}

/**
 * Grava a posição no rascunho, sem tocar no resto dele (o texto, a aparência e
 * o que mais a gestão guardar lá). Diz se gravou.
 */
export function gravarPosicaoNoRascunho(posicao: PosicaoDoTooltip): boolean {
  const objeto = lerObjetoDoRascunho()
  if (objeto === undefined) return false
  try {
    window.sessionStorage.setItem(
      CHAVE_DO_RASCUNHO,
      JSON.stringify({ ...objeto, posicao: { x: posicao.x, y: posicao.y } })
    )
    return true
  } catch {
    return false
  }
}

// ── O tooltip em teste ───────────────────────────────────────────────────────

/** O tooltip como `GET /v1/admin/tooltips/{id}` o devolve; só o que o teste usa. */
export interface TooltipDaGestao {
  id: number
  trechos: Trecho[]
  texto: string
  modo: ModoDoTooltip
  tamanho: TamanhoDoTooltip
  /** Nula no tooltip fixo, que mora no ícone *i* de um local da tela. */
  posicao: PosicaoDoTooltip | null
  situacao: string
}

export function lerTooltipDaGestao(corpo: unknown): TooltipDaGestao | undefined {
  if (typeof corpo !== 'object' || corpo === null) return undefined
  const campos = corpo as Record<string, unknown>
  if (typeof campos.id !== 'number') return undefined
  const trechos = lerTrechos(campos.trechos)
  const modo = MODOS.find((m) => m === campos.modo)
  const tamanho = TAMANHOS.find((t) => t === campos.tamanho)
  const posicao = lerPosicao(campos.posicao)
  if (trechos === undefined || modo === undefined || tamanho === undefined) return undefined
  if (posicao === undefined) return undefined
  return {
    id: campos.id,
    trechos,
    texto: trechos.map((t) => t.texto).join(''),
    modo,
    tamanho,
    posicao,
    situacao: typeof campos.situacao === 'string' ? campos.situacao : '',
  }
}

/** A resposta não tem a forma combinada com a API. */
export class TooltipInvalido extends Error {
  constructor() {
    super('O tooltip da gestão não tem a forma combinada')
    this.name = 'TooltipInvalido'
  }
}

/**
 * O que a faixa oferece: concluir o teste (administrador, tooltip que aguarda
 * teste), dizer que ele já foi concluído, dizer que só o administrador o
 * conclui (o editor de conteúdo também vê o tooltip) ou nada (já publicado).
 */
export type AcaoDoTeste = 'concluir' | 'concluido' | 'so-administrador' | 'nenhuma'

function acaoDoTeste(podeTestar: boolean, situacao: string): AcaoDoTeste {
  if (situacao === 'testado') return 'concluido'
  if (situacao !== 'aguardando-teste') return 'nenhuma'
  return podeTestar ? 'concluir' : 'so-administrador'
}

/** O estado do tooltip em teste, pronto para a faixa e para o desenho. */
export type TooltipEmTeste =
  | { estado: 'carregando' }
  | { estado: 'recusado'; erro: unknown }
  | { estado: 'pronto'; tooltip: TooltipDaGestao; acao: AcaoDoTeste }

/** A raiz das consultas dos tooltips da gestão: concluir o teste relê todas. */
const CHAVE_DA_GESTAO = ['admin', 'tooltips'] as const

/**
 * O tooltip em teste e, da lista da gestão, se a sessão testa e publica. Sem
 * id não pede nada: o observatório comum nunca toca nas rotas da gestão.
 */
export function useTooltipEmTeste(id: number | undefined): TooltipEmTeste | undefined {
  const item = useQuery({
    queryKey: [...CHAVE_DA_GESTAO, id],
    enabled: id !== undefined,
    queryFn: async () => {
      const tooltip = lerTooltipDaGestao(await apiFetch<unknown>(`/v1/admin/tooltips/${id}`))
      if (tooltip === undefined) throw new TooltipInvalido()
      return tooltip
    },
  })
  // A lista da gestão é a que diz se a sessão testa e publica; a tela da
  // gestão a lê com outra forma, então a chave é outra.
  const permissao = useQuery({
    queryKey: [...CHAVE_DA_GESTAO, 'pode-testar'],
    enabled: id !== undefined,
    queryFn: async () => {
      const corpo = await apiFetch<unknown>('/v1/admin/tooltips')
      return typeof corpo === 'object' && corpo !== null && 'podeTestarEPublicar' in corpo
        ? corpo.podeTestarEPublicar === true
        : false
    },
  })

  if (id === undefined) return undefined
  if (item.isError) return { estado: 'recusado', erro: item.error }
  if (permissao.isError) return { estado: 'recusado', erro: permissao.error }
  if (item.data === undefined || permissao.data === undefined) return { estado: 'carregando' }
  return {
    estado: 'pronto',
    tooltip: item.data,
    acao: acaoDoTeste(permissao.data, item.data.situacao),
  }
}

/** `POST /v1/admin/tooltips/{id}/teste`: leva o tooltip a testado; a gestão é relida. */
export function useConcluirTesteDoTooltip(id: number) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch<unknown>(`/v1/admin/tooltips/${id}/teste`, { method: 'POST' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE_DA_GESTAO }),
  })
}
